import type { TrackingInteractiveQuestionType } from '@genially/contracts';

import { nonScoreableTrackingInteractiveQuestionsList } from '../../../../../../../../shared/types';

/** Tracking flags of an answered widget. Native interactive questions have none. */
export type AnsweredWidgetTracking = {
  isScoreable?: boolean;
  isCategorizable?: boolean;
};

export enum SidebarBehaviourOnAnswer {
  /** Aggregated results are available right away (surveys, categorizable widgets). */
  OpenSummary = 'openSummary',
  /** The host still has to resolve the question before there is anything to show. */
  WaitForHost = 'waitForHost',
  /** Nothing to show and nothing to wait for. */
  DoNothing = 'doNothing',
}

/**
 * Decides what the player's sidebar does when their answer is registered.
 *
 * The key case is the last one: a widget that is neither scoreable nor categorizable — a
 * completion-only activity such as a memory match — offers the host no resolve action, so
 * waiting on it would never end.
 */
export const decideSidebarBehaviourOnAnswer = ({
  type,
  isEmptyAnswer,
  widgetTracking,
}: {
  type: TrackingInteractiveQuestionType;
  isEmptyAnswer?: boolean;
  /** Omitted when the answered question is a native one rather than a widget. */
  widgetTracking?: AnsweredWidgetTracking;
}): SidebarBehaviourOnAnswer => {
  if (
    nonScoreableTrackingInteractiveQuestionsList.includes(type) ||
    widgetTracking?.isCategorizable === true
  ) {
    return SidebarBehaviourOnAnswer.OpenSummary;
  }

  if (isEmptyAnswer) {
    return SidebarBehaviourOnAnswer.DoNothing;
  }

  if (widgetTracking && widgetTracking.isScoreable !== true) {
    return SidebarBehaviourOnAnswer.DoNothing;
  }

  return SidebarBehaviourOnAnswer.WaitForHost;
};
